import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  Fragment,
  createElement,
  forwardRef,
  useEffect,
  useState,
} from "react";

export const Modal = forwardRef((props, ref) => {
  const {
    isOpen: defaultOpen = false,
    children,
    disabled = false,
    title,
    description,
    body,
    renderBody,
    scale = "fullscreen",
    closeOnBackdrop = true,
    onClose,
    onOpen,
    hideCloseButton = false,
  } = props || {};

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    defaultOpen && openModal();
  }, [defaultOpen]);

  const closeModal = () => {
    setIsOpen(false);
    onClose?.();
  };

  const openModal = () => {
    onOpen?.();
    setIsOpen(true);
  };

  const sizeMap = {
    sm: "w-full max-w-md",
    smd: "w-full max-w-[628px]",
    md: "w-full max-w-[1032px]",
    lg: "w-full max-w-[1250px]",
    fullscreen: "fixed inset-0 h-[100dvh] w-full overflow-y-auto",
  };
  const scaleClass = sizeMap[scale];
  const panelViewportClass =
    scale === "fullscreen"
      ? "max-h-[100dvh] rounded-none sm:rounded-2xl"
      : "max-h-[calc(100dvh-1rem)] rounded-2xl sm:max-h-[calc(100dvh-2rem)]";

  // Extract body from props to avoid circular reference when spreading
  const { body: _, renderBody: __, ...bodyProps } = props || {};

  return (
    <div
      ref={ref}
      onKeyDown={e => e.stopPropagation()}
      // onClick={(e) => e.stopPropagation()}
      onFocus={e => e.stopPropagation()}
      onMouseOver={e => e.stopPropagation()}
    >
      <div onClick={!disabled ? openModal : null}>{children}</div>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-hidden"
          onClose={closeOnBackdrop ? closeModal : () => {}}
        >
          <div className="flex min-h-[100dvh] items-center justify-center p-2 text-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-80"
              leave="ease-in duration-200"
              leaveFrom="opacity-80"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-300 opacity-75" />
            </Transition.Child>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div
                className={`${scaleClass} ${panelViewportClass} transform overflow-y-auto bg-white p-4 text-left align-middle shadow-xl transition-all sm:p-6`}
                data-testid="modal-child"
              >
                <div className="flex h-full flex-col">
                  {!hideCloseButton && (
                    <div className="flex justify-between">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        {title}
                      </Dialog.Title>
                      <button
                        type="button"
                        className="-mr-2 -mt-2 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
                        aria-label="Close modal"
                        onClick={closeModal}
                      >
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  {description && (
                    <div className="mt-2 flex">
                      <p className="text-sm text-gray-500">{description}</p>
                    </div>
                  )}

                  {/* Render callbacks preserve their children across parent updates. */}
                  {renderBody
                    ? renderBody({ closeModal, isOpen, ...bodyProps })
                    : typeof body === "function"
                      ? createElement(body, {
                          closeModal,
                          isOpen,
                          ...bodyProps,
                        })
                      : body}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
});

Modal.displayName = "Modal";
